/**
 * trackPageEvent: ウィジェット表示（WIDGET_VIEW）・JSエラー（ERROR）の分析イベントをHTTP送信する。
 * US-D2-05、FR-Y6。BR-U6-6: Phase 1では暫定エンドポイントへ実際にHTTP送信する。
 * fire-and-forget送信。失敗してもUXをブロックしない。
 *
 * <p>2026-08-04改訂（U13 site_type統一Unit、BR-U8-21・BR-U8-22）: 電子ブック閲覧のページ推移・
 * 滞在時間・離脱の詳細トラッキング（旧PAGE_TRANSITION/PAGE_DWELL_TIME/PAGE_EXIT）は廃止した。
 * 表示トリガー監視（到達率（廃止済み）判定用のページ推移通知）は分析イベント送信とは別関数
 * （{@link notifyPageTransitionToMonitor}）に責務分離済み（Step12）。本ファイルのtrackPageEvent
 * 自体はdisplayConditionMonitorのインスタンスを引数で受け取らない（モジュールグローバルな関数への
 * 依存を持たない、NFR Design 1.2準拠のタグ単位の隔壁化はnotifyPageTransitionToMonitor側で維持）。
 */

import { relayClient } from "../core/relayClient";
import { logClientWarning } from "../core/errorHandler";
import type { DisplayConditionMonitor } from "../w1-survey/displayConditionMonitor";
import type { PageEventType, PageEventPayload, LoginPromptReason } from "../types";
import { getOrCreateSessionId } from "./sessionContext";

/**
 * トラッキングイベントを送信する（BR-U6-6）。
 *
 * <p>2026-09-28（効果分析ログ、BR-AN-1）: WIDGET_VIEW/ERROR に加え、内部社員向け効果分析用の
 * ファネルイベント（{@link PageEventType} 参照）も本関数で送信する（各ラッパー経由）。
 *
 * <p>表示トリガー監視（到達率（廃止済み）判定用のページ推移通知）は
 * {@link notifyPageTransitionToMonitor}へ責務分離済み（Step12、U13 site_type統一Unit）。
 * 本関数は分析イベント送信（HTTP送信）のみを担う。
 */
export function trackPageEvent(eventType: PageEventType, payload: PageEventPayload): void {
  try {
    relayClient.trackPageEvent({ eventType, payload });
  } catch {
    logClientWarning("trackPageEvent failed");
  }
}

/**
 * ページ推移を表示条件監視（displayConditionMonitor、到達率（廃止済み）判定用）へ通知する。
 *
 * <p>分析イベント送信（{@link trackPageEvent}）とは独立して呼び出せる（Step12、責務分離）。
 * ホスト側（電子ブックビューア等のCMSスクリプト）が`window.SpicaWidget.notifyPageTransition`
 * （core/hostApi.ts）経由で呼び出す想定。
 *
 * @param pageNumber 現在のページ番号
 * @param totalPages 総ページ数
 * @param monitor    呼び出し元タグインスタンスのdisplayConditionMonitor（entry.tsで生成したインスタンス）
 */
export function notifyPageTransitionToMonitor(
  pageNumber: number,
  totalPages: number,
  monitor?: DisplayConditionMonitor
): void {
  monitor?.notifyPageTransition(pageNumber, totalPages);
}

/**
 * JSエラー・未処理例外の最小限検知（NFR-U6-MAINT2）。
 * trackPageEventにエラーイベントを含める形で最小限の検知に留める。
 */
export function trackClientError(message: string, identifier?: string): void {
  trackPageEvent("ERROR", { message, identifier });
}

// ====================================================================
// 効果分析ログ（内部社員向け、2026-09-28追加、BR-AN-1〜14）
// 各ラッパーは sessionId（1訪問の相関キー、BR-AN-3(b)）を必ず付与してから送信する。
// 送信は既存 trackPageEvent（fire-and-forget、try/catch）を用いるため、失敗してもUXを阻害しない
// （フェイルオープン、BR-AN-10/NFR-AN-AVAIL1）。iprosUuid はタグでは送らず中継が付与する。
//
// 2026-09-29 レビュー反映:
// - WIDGET_OPEN は WIDGET_VIEW と発火タイミングが重複するため廃止（起点は entry.ts の WIDGET_VIEW
//   に sessionId を付与して一本化）。
// - WIDGET_CLOSE は縮小トグル化に伴い WIDGET_MINIMIZE（縮小＝離脱意思）へ改名。openedBeforeClose は廃止。
// - cancelReason（自由記述）は PII 混入を避けるため送らない（appointmentId から DB 参照可能）。
// - companyRootId は WIDGET_VIEW と同様に各イベントで呼び出し側が付与する（テナント境界確定に必須、
//   NFR-A3。中継は透過のみで解決しないため、タグが CV 単位の companyRootId を載せる）。
// ====================================================================

/**
 * 効果分析ログを送信する共通ヘルパー。sessionId を付与してから trackPageEvent を呼ぶ。
 * sessionId 付与漏れを防ぐため、効果分析ログの送信は必ず本関数（または各ラッパー）を経由する。
 */
export function trackAnalyticsEvent(eventType: PageEventType, payload: PageEventPayload = {}): void {
  trackPageEvent(eventType, { ...payload, sessionId: getOrCreateSessionId() });
}

/** アンケート回答選択（イベントB）。selectedChoiceId/surveyId を付与する。 */
export function trackSurveyAnswer(payload: PageEventPayload = {}): void {
  trackAnalyticsEvent("SURVEY_ANSWER", { ...payload, funnelStep: "SURVEY" });
}

/** 商談形式選択（イベントC）。meetingType（ONLINE/ON_SITE）を付与する。 */
export function trackMeetingTypeSelect(payload: PageEventPayload = {}): void {
  trackAnalyticsEvent("MEETING_TYPE_SELECT", { ...payload, funnelStep: "SELECT" });
}

/** 日時候補選択（イベントC）。slotSelected=true を付与する（日時実値は載せない、BR-AN-8）。 */
export function trackSlotSelect(payload: PageEventPayload = {}): void {
  trackAnalyticsEvent("SLOT_SELECT", { ...payload, slotSelected: true, funnelStep: "SELECT" });
}

/** アポリクエスト送信成功（イベントG＝ゴール）。appointmentId を付与する（確定時）。 */
export function trackAppointmentSubmit(payload: PageEventPayload = {}): void {
  trackAnalyticsEvent("APPOINTMENT_SUBMIT", { ...payload, funnelStep: "SUBMIT" });
}

/** アポリクエスト送信失敗（イベントD）。failureKind/businessErrorCode を付与する（BR-AN-6）。 */
export function trackAppointmentSubmitFailed(payload: PageEventPayload = {}): void {
  trackAnalyticsEvent("APPOINTMENT_SUBMIT_FAILED", { ...payload, funnelStep: "SUBMIT" });
}

/** ログイン要求プロンプト表示（イベントE＝最優先、BR-AN-5）。loginPromptReason を付与する。 */
export function trackLoginPrompt(loginPromptReason: LoginPromptReason, payload: PageEventPayload = {}): void {
  trackAnalyticsEvent("LOGIN_PROMPT", { ...payload, loginPromptReason });
}

/** 閲覧ユーザーによるアポキャンセル成功（イベントK）。appointmentId/cancelEntryPoint 等を付与する。 */
export function trackAppointmentCancel(payload: PageEventPayload = {}): void {
  trackAnalyticsEvent("APPOINTMENT_CANCEL", { ...payload, funnelStep: "CANCEL" });
}

/** ウィジェットを縮小トグルで畳んだ（離脱意思、イベントL、BR-AN-14）。 */
export function trackWidgetMinimize(payload: PageEventPayload = {}): void {
  trackAnalyticsEvent("WIDGET_MINIMIZE", { ...payload, funnelStep: "LEAVE" });
}
